import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DatePicker, TimePicker, FieldGroup } from '@Sage/hoi-poi-ui';

import { useSelector } from 'react-redux';
import classnames from 'classnames';
import { getLiteral } from 'utils/getLiteral';
import { formatDate } from 'utils/dates';

const DateTime = ({ className, value, onChange, label, isReadOnly, isDisabled, error }) => {
    const lang = useSelector((state) => state.config?.userData?.langISOInterface);
    const [date, setDate] = useState(value ? new Date(value) : null);
    const [time, setTime] = useState(value ? new Date(value) : null);

    const parseTimeString = (timeStr) => {
        if (!timeStr) return { hours: 0, minutes: 0 };

        return {
            hours: timeStr.getHours(),
            minutes: timeStr.getMinutes(),
        };
    };

    useEffect(() => {
        if (value) {
            const newDate = new Date(value);
            setDate(newDate);
            setTime(newDate);
        } else {
            setDate(null);
            setTime(null);
        }
    }, [value]);

    const getCombinedDateTime = useCallback((newDate, newTime) => {
        if (!newDate || !newTime) return null;

        const combined = new Date(newDate);
        const { hours, minutes } = parseTimeString(newTime);

        combined.setHours(hours, minutes, 0, 0);

        return combined;
    }, []);

    const handleDateChange = useCallback(
        (newDate) => {
            setDate(newDate);
            if (newDate && time) {
                const combinedDateTime = getCombinedDateTime(newDate, time);
                onChange?.(combinedDateTime);
            } else if (!newDate) {
                onChange?.(null);
            }
        },
        [getCombinedDateTime, onChange, time],
    );

    const handleTimeChange = useCallback(
        (newTime) => {
            setTime(newTime);
            if (date && newTime) {
                const combinedDateTime = getCombinedDateTime(date, newTime);
                onChange?.(combinedDateTime);
            } else if (!newTime) {
                onChange?.(null);
            }
        },
        [getCombinedDateTime, onChange, date],
    );

    const fieldGroupProps = useMemo(() => {
        return {
            inputs: [DatePicker, TimePicker],
            inputProps: [
                {
                    value: date,
                    onChange: handleDateChange,
                    lang,
                    calendarButtonLabel: getLiteral('label_today'),
                    placeholder: getLiteral('label_selectone'),
                    formatDate: (date) => formatDate(date, 'L'),
                    isReadOnly,
                    isDisabled,
                    className: 'fm-field-date-time__date-picker',
                },
                {
                    value: time,
                    onChange: handleTimeChange,
                    isReadOnly,
                    isDisabled,
                    className: 'fm-field-date-time__time-picker',
                    placeholder: getLiteral('label_selectone'),
                },
            ],
        };
    }, [date, handleDateChange, handleTimeChange, isDisabled, isReadOnly, lang, time]);

    return (
        <div className={classnames('fm-field-date-time', className)}>
            <FieldGroup label={label} error={error} isFullWidth {...fieldGroupProps} />
        </div>
    );
};

export default DateTime;
